import React from 'react';
import { NewTestTab } from './NewTestTab';
import { RuleBasedActionPlanTab } from './RuleBasedActionPlanTab';
import { AIActionPlanTab } from './AIActionPlanTab';
import WeeklyDosingTab from './WeeklyDosingTab';
import { LsiTab } from './LsiTab';
import { LogAndChartsTab } from './LogAndChartsTab';
import { Chem101Tab } from './Chem101Tab';
import { ProductGuideTab } from './ProductGuideTab';
import { MaintenanceTab } from './MaintenanceTab';
import type { TestResult, TestLogEntry, AIActionPlanStep, WaterBodyType } from '../types';
import type { WeeklyDosingGuide } from '../services/geminiService';

interface TabContentProps {
  activeTab: string;
  waterBody: WaterBodyType;
  volume: number;
  setVolume: (v: number) => void;
  log: TestLogEntry[];
  setLog: React.Dispatch<React.SetStateAction<TestLogEntry[]>>;
  latestTestResult: TestResult | null;
  currentAIPlan: AIActionPlanStep[] | null;
  isLoadingAIPlan: boolean;
  errorAIPlan: string | null;
  onSaveTest: (results: TestResult) => void;
  onRerunAnalysis: (entry: TestLogEntry, targetTab: 'ai_action_plan' | 'action_plan') => void;
  onUpdateLogEntry: (entry: TestLogEntry) => void;
  onDeleteLogEntry: (id: number) => void;
  onSaveAIPlan: (plan: AIActionPlanStep[]) => Promise<void>;
  onSaveWeeklyGuide: (guide: WeeklyDosingGuide) => Promise<void>;
}

export const TabContent: React.FC<TabContentProps> = ({
  activeTab, waterBody, volume, setVolume, log, setLog,
  latestTestResult, currentAIPlan, isLoadingAIPlan, errorAIPlan,
  onSaveTest, onRerunAnalysis, onUpdateLogEntry, onDeleteLogEntry,
  onSaveAIPlan, onSaveWeeklyGuide,
}) => {
  switch (activeTab) {
    case 'new_test':
      return (
        <NewTestTab
          poolVolume={volume}
          setPoolVolume={setVolume}
          onSave={onSaveTest}
          waterBody={waterBody}
        />
      );
    case 'action_plan':
      return <RuleBasedActionPlanTab results={latestTestResult} poolVolume={volume} waterBody={waterBody} />;
    case 'ai_action_plan':
      return (
        <AIActionPlanTab
          plan={currentAIPlan}
          isLoading={isLoadingAIPlan}
          error={errorAIPlan}
          poolVolume={volume}
          onSavePlan={onSaveAIPlan}
        />
      );
    case 'weekly_dosing':
      return (
        <WeeklyDosingTab
          latestResults={latestTestResult}
          log={log}
          poolVolume={volume}
          waterBody={waterBody}
          onSaveGuide={onSaveWeeklyGuide}
        />
      );
    case 'maintenance':
      return <MaintenanceTab log={log} poolVolume={volume} waterBody={waterBody} />;
    case 'lsi':
      return <LsiTab results={latestTestResult} />;
    case 'log_charts':
      return (
        <LogAndChartsTab
          log={log}
          setLog={setLog}
          poolVolume={volume}
          onRerunAnalysis={onRerunAnalysis}
          onUpdate={onUpdateLogEntry}
          onDelete={onDeleteLogEntry}
        />
      );
    case 'chem_101':
      return <Chem101Tab waterBody={waterBody} />;
    case 'product_guide':
      return <ProductGuideTab waterBody={waterBody} />;
    default:
      return null;
  }
};